import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { PopupRangeStore, PopupCalendarConfig } from '../../utils';
import { DateUtilities } from '../../utils/date-utilities';
import { PopupRangeService } from '../../utils/popup-range.service';

@Component({
    selector: 'aa-popup-navigation',
    templateUrl: './popup-navigation.component.html',
    styleUrls: [
        '../shared.scss',
        './popup-navigation.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupNavigationComponent implements OnInit, OnDestroy
{
    @Input() isSinceCalendar: boolean;
    public cfg: PopupCalendarConfig = new PopupCalendarConfig();
    public displayRange: string = '';
    public isPrevYearDisabled: boolean = false;
    public isPrevMonthDisabled: boolean = false;
    public isNextMonthDisabled: boolean = false;
    public isNextYearDisabled: boolean = false;
    private _firstDayOfCalendar: DateTime;
    get firstDayOfCalendar ()
    {
        return this._firstDayOfCalendar;
    }
    set firstDayOfCalendar (value)
    {
        this._firstDayOfCalendar = value;
        this.lastDayOfCalendar = this._firstDayOfCalendar.endOf('month');
        this.displayRange = `${this._firstDayOfCalendar.toFormat(this.dateFormat)}`;
    }
    private _lastDayOfCalendar: DateTime;
    get lastDayOfCalendar ()
    {
        return this._lastDayOfCalendar;
    }
    set lastDayOfCalendar (value)
    {
        this._lastDayOfCalendar = value;
    }
    private dateFormat: string = 'LLL yyyy';
    private destroyator = new Subject();
    private currentSince: DateTime = null;
    private currentUntil: DateTime = null;

    constructor (
        private popupRangeService: PopupRangeService
    )
    {
    }

    ngOnInit ()
    {
        this.popupRangeService.rangeConfig
            .pipe(
                tap((prs: PopupRangeStore) =>
                {
                    if (this.isSinceCalendar)
                    {
                        if (this.currentSince === null || !DateUtilities.isEqual(this.currentSince, prs.currentSince))
                        {
                            this.currentSince = prs.currentSince;
                            this.firstDayOfCalendar = prs.currentSince.startOf('month');
                        }

                        this.isPrevYearDisabled = DateUtilities.isStrictlyBefore(this.firstDayOfCalendar.minus({ year: 1 }), prs.minDate.startOf('month'));
                        this.isPrevMonthDisabled = DateUtilities.isStrictlyBefore(this.firstDayOfCalendar.minus({ day: 1 }), prs.minDate.startOf('month'));
                        this.isNextMonthDisabled = DateUtilities.isStrictlyAfter(this.lastDayOfCalendar.plus({ day: 1 }), prs.lastDayUntilCalendar);
                        this.isNextYearDisabled = DateUtilities.isStrictlyAfter(this.lastDayOfCalendar.plus({ year: 1 }), prs.lastDayUntilCalendar);
                    }
                    else
                    {
                        if (this.currentUntil === null || !DateUtilities.isEqual(this.currentUntil, prs.currentUntil))
                        {
                            this.currentUntil = prs.currentUntil;
                            this.firstDayOfCalendar = prs.currentUntil.startOf('month');
                        }

                        this.isPrevYearDisabled = DateUtilities.isStrictlyBefore(this.firstDayOfCalendar.minus({ year: 1 }), prs.firstDaySinceCalendar);
                        this.isPrevMonthDisabled = DateUtilities.isStrictlyBefore(this.firstDayOfCalendar.minus({ day: 1 }), prs.firstDaySinceCalendar);
                        this.isNextMonthDisabled = DateUtilities.isStrictlyAfter(this.lastDayOfCalendar.plus({ day: 1 }), prs.maxDate.endOf('month'));
                        this.isNextYearDisabled = DateUtilities.isStrictlyAfter(this.lastDayOfCalendar.plus({ year: 1 }), prs.maxDate.endOf('month'));
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnDestroy ()
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    onPrevYear ()
    {
        this.firstDayOfCalendar = this.firstDayOfCalendar.minus({ year: 1 });
        this.setGlobal();
    }

    onPrevMonth ()
    {
        this.firstDayOfCalendar = this.firstDayOfCalendar.minus({ month: 1 });
        this.setGlobal();
    }

    onNextMonth ()
    {
        this.firstDayOfCalendar = this.firstDayOfCalendar.plus({ month: 1 });
        this.setGlobal();
    }

    onNextYear ()
    {
        this.firstDayOfCalendar = this.firstDayOfCalendar.plus({ year: 1 });
        this.setGlobal();
    }

    private setGlobal ()
    {
        if (this.isSinceCalendar)
        {
            this.popupRangeService.firstDaySinceCalendar = this.firstDayOfCalendar;
        }
        else
        {
            this.popupRangeService.firstDayUntilCalendar = this.firstDayOfCalendar;
        }
    }
}
